<template lang="pug">
	.user-profile
		Loader(v-if="isLoading", :fullscreen="true")
		.flex-col.space(v-else-if="userInfo")
			.flex-row.justify.space.align-center
				.font-large.padding {{$tCaption('Profile')}}
				.actions.flex-row.space.lr-padding
					DxButton(:text="$tCaption('Change password')", icon="fas fa-key", stylingMode="text", @click="openChangePasswordDialog")
			dx-validation-group.flex-row.space-large.details-segment(ref="validationGroup")
				.flex-col.space.align-center
					.user-image-holder
						.user-image(v-if="$userInfo.user && $userInfo.user.HasPhoto", :style="{ background: `url(${photoUrl})` }")
						.user-image(v-else, :style="{ background: `url(${require('assets/default-user-image.png')})` }")
					FileUploader(:text="$tCaption('Upload photo')", icon="fas fa-cloud-upload-alt", @file-selected="onPhotoSelected", @upload="onPhotoChange", :popupConfig="{ height: 520, width: 520 }", @popup="photoData = null")
						.flex-row.align-center(v-if="photoData", slot="bottom-content")
							VueCropper.user-profile.cropper-element(ref="cropper",
								:aspectRatio="1",
								:src="photoData")
				.flex-col.space-large
					.flex-col.space
						.font-big.upper {{$auth.company().Name}}
					.flex-col.field.space
						.font-small.upper.grey {{$tCaption('Name')}}
						dx-text-box(
							:value.sync="userInfo.Contact.Title"
							:disabled="isLoading"
							width="100%"
						)
							dx-validator
								dx-required-rule(message="Title is required")
					.flex-col.field.space
						.font-small.upper.grey {{$tCaption('Email')}}
						dx-text-box(
							:value.sync="userInfo.Contact.Email"
							:disabled="true"
							width="100%"
						)
							dx-validator
								dx-required-rule(message="Email is required")
					.flex-col.field.space
						.font-small.upper.grey {{$tCaption('Phone')}}
						dx-text-box(
							:value.sync="userInfo.Contact.Phone",
							:disabled="isLoading",
							width="100%",
							mode="tel",
							mask="+0 (000) 000-0000"
						)
					.flex-col.field.space
						.font-small.upper.grey {{$tCaption('Language')}}
						DxSelectBox(
							:value="userInfo.Contact.LanguageCode",
							:disabled="isLoading",
							width="100%",
							:dataSource="locales",
							displayExpr="displayName",
							valueExpr="locale",
							:searchEnabled="true",
							@valueChanged="onLangChanged"
						)
				Popup(v-if="changePassword.visible", :popupVisible.sync="changePassword.visible", :width="400", :height="400")
					.flex-col.space-large
						.font-big.padding {{$tCaption('Change password')}}
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Old password')}}
							dx-text-box(
								:value.sync="changePassword.oldPassword"
								width="100%"
								mode="password"
								:disabled="changePassword.changing"
							)
								dx-validator
									dx-required-rule(:message="$tCaption('Old password is required')")
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('New password')}}
							dx-text-box(
								:value.sync="changePassword.password"
								width="100%"
								mode="password"
								:disabled="changePassword.changing"
							)
								dx-validator
									dx-required-rule(:message="$tCaption('Password is required')")
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Enter password again')}}
							dx-text-box(
								:value.sync="changePassword.passwordAgain"
								width="100%"
								mode="password"
								:disabled="changePassword.changing"
							)
								dx-validator
									dx-required-rule(:message="$tCaption('Enter password again')")
									dx-custom-rule(:validation-callback="() => !!(changePassword.passwordAgain === changePassword.password)", :message="$tCaption('Entered values are different')")
					.flex-row.space(slot="actions-right")
						DxButton(:text="$tCaption('Cancel', '@actions')", @click="changePassword.visible = false")
						DxButton(:text="$tCaption('Commit', '@actions')", type="default", @click="doChangePassword", :disabled="changePassword.changing")
</template>

<script>
	import DxButton from 'devextreme-vue/button';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxDateBox from 'devextreme-vue/date-box';
	import Loader from 'app-core/Loader.vue';
	import DxValidationGroup from 'devextreme-vue/validation-group';
	import DxValidator, { DxRequiredRule, DxCustomRule } from 'devextreme-vue/validator';
	import DxSelectBox from 'devextreme-vue/select-box';
	import VueCropper from 'vue-cropperjs';
	import 'cropperjs/dist/cropper.css';
	import FileUploader from 'app-core/FileUploader.vue';
	import Popup from 'app-core/Popup.vue';

	export default {
		translationCategory: 'user.Profile',
		name: 'user-profile',
		components: {
			DxButton, DxTextBox, Loader, DxDateBox, DxSelectBox,
			DxValidationGroup, DxValidator, DxRequiredRule, DxCustomRule, VueCropper, FileUploader, Popup,
		},
		data() {
			return {
				userInfo: {
					Contact: {
						LanguageCode: null
					}
				},
				isLoading: false,
				isSaving: false,
				photoData: null,
				photoUrl: null,
				changePassword: {
					visible: false,
					oldPassword: null,
					password: null,
					passwordAgain: null,
					changing: false
				}
			};
		},
		watch: {
			userInfo: {
				handler() {
					if (!this.isLoading && !this.isSaving) {
						if (__DEBUG__)
							console.debug('User changed', arguments);
						this.save();
					}
				},
				deep: true
			}
		},
		computed: {
			locales() {
				return this.$Data.langmapItems();
			}
		},
		mounted() {
			if (__DEBUG__)
				window.profile = this;
			this.loadProfile();
		},
		methods: {
			onLangChanged(e) {
				if (__DEBUG__)
					console.debug('Lang changed', { e });
				this.userInfo.Contact.LanguageCode = e.value;
			},
			openChangePasswordDialog(){
				this.changePassword.visible = false;
				this.changePassword.oldPassword = null;
				this.changePassword.password = null;
				this.changePassword.passwordAgain = null;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.changePassword.visible = true;
				});
			},
			doChangePassword(e) {
				if (!e.validationGroup.validate().isValid) {
					return;
				}
				this.changePassword.changing = true;
				this.$Data.getExternalApi('Security/ChangeCurrentUserPassword', `oldPassword=${this.changePassword.oldPassword}&newPassword=${this.changePassword.password}`)
					.then(r => {
						if (r.Success) {
							this.$notifySuccess(this.$tCaption('Password changed'));
							this.changePassword.visible = false;
						} else {
							this.$notifyError(r.ErrorMessage);
						}
					})
					.finally(() => this.changePassword.changing = false);
			},
			setPhotoUrl(rand) {
				this.photoUrl = `${this.$Data.getCurrentUserPhoto()}${rand ? `&ver=${Math.random()}` : ''}`;
				if (rand)
					this.$auth.userPhoto(this.photoUrl);
			},
			loadProfile() {
				this.isLoading = true;
				return this.$Data.getCurrentUserProfile().then(r => {
					_.assign(this.userInfo, r);
					this.setPhotoUrl();
				}).finally(() => this.isLoading = false);
			},
			save() {
				if (this.isSaving || this.isLoading) return;
				if (__DEBUG__)
					console.debug('Saving', this.isSaving);

				this.isSaving = true;

				return this.$Data.postExternalApi('Security/UpdateCurrentUserProfile', this.userInfo.Contact).then(r => {
					if (this._isDestroyed) return;
					if (r.Success) {
						this.$notifySuccess(this.$tCaption('Profile updated'));
					} else {
						this.$notifyError(r.ErrorMessage);
					}
				}).finally(() => {
					this.isSaving = false;
				});
			},
			onPhotoSelected(v) {
				if (__DEBUG__)
					console.log('On photo selected', v);
				this.photoData = null;
				var fr = new FileReader;
				fr.readAsDataURL(v[0]);
				fr.onloadend = () => {
					this.photoData = fr.result;
				};
			},
			onPhotoChange() {
				if (__DEBUG__)
					console.debug('On photo change');
				var img = this.$refs.cropper.getCroppedCanvas({ width: 320, height: 320 }).toDataURL(data => console.log(data)).split('base64,')[1];
				this.$Data.postExternalApi('Service/SetObjectImage', {
					ObjectId: this.$auth.userId(),
					ObjectType: 1,
					Base64Content: img
				}).then(r => {
					if (__DEBUG__)
						console.debug('Photo uploaded', { r });
					if (r.Success) {
						this.setPhotoUrl(true);
					}
				});
			}
		}
	};
</script>
<style lang='scss'>
	.user-profile {
		.user-image-holder {
			height: 280px;
			width: 280px;
			border-radius: 140px;
			background: rgba(0, 0, 0, 0.03);
			overflow: hidden;

			border: 1px solid rgba(0, 0, 0, 0.01);
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

			.user-image {
				height: 100%;
				width: 100%;
				background-size: contain !important;
			}
		}

		&.cropper-element {
			height: 280px;
			width: 100%;
		}
	}
</style>
